import type { ItemDetail, Link } from "../api/types.gen.ts";
import { mergePatch } from "./diff.ts";

/** An item being edited. */
interface Draft {
  title: string;
  entity: string;
  data: Record<string, unknown>;
  refs: Record<string, Link[]>;
}

/** What changed from `a` to `b`: "title", "entity", "data.<key>", "refs.<key>". */
function changes(a: Draft, b: Draft): string[] {
  return [
    ...(a.title === b.title ? [] : ["title"]),
    ...(a.entity === b.entity ? [] : ["entity"]),
    ...Object.keys(mergePatch(a.data, b.data) ?? {}).map((k) => `data.${k}`),
    ...Object.keys(mergePatch(a.refs, b.refs) ?? {}).map((k) => `refs.${k}`),
  ];
}

/** `onto` with the keys `from` changed copied over (or removed, where `from` removed them). */
function carry<T>(from: Record<string, T>, onto: Record<string, T>, keys: readonly string[]): Record<string, T> {
  const out = { ...onto };
  for (const k of keys) {
    const v = from[k];
    if (v === undefined) {
      delete out[k];
    } else {
      out[k] = v;
    }
  }
  return out;
}

export const draftOf = (i: ItemDetail): Draft => ({
  title: i.title ?? "",
  entity: i.entity ?? "",
  data: { ...i.data },
  refs: { ...i.refs },
});

/**
 * Local edits (`draft` against `base`) replayed onto a newer version `next`, when the two changed different
 * things (e.g. a file attached meanwhile vs. a price being edited); null when they touched the same title, entity,
 * data key or relationship.
 */
export function rebase(base: Draft, draft: Draft, next: Draft): Draft | null {
  const mine = changes(base, draft);
  const theirs = new Set(changes(base, next));
  if (mine.some((c) => theirs.has(c))) {
    return null;
  }
  const keys = (prefix: string) => mine.filter((c) => c.startsWith(prefix)).map((c) => c.slice(prefix.length));
  return {
    title: mine.includes("title") ? draft.title : next.title,
    entity: mine.includes("entity") ? draft.entity : next.entity,
    data: carry(draft.data, next.data, keys("data.")),
    refs: carry(draft.refs, next.refs, keys("refs.")),
  };
}

export type { Draft };
